import { Suspense } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
// layouts
import { useSentryRoutes } from 'src/sentryInstrument';
import IntervalComponentV3 from 'src/components/NotificationHandler/IntervalComponentV3';
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import { CompactLayout } from '../layouts/compact';
// guards
import { GuestGuard, AuthGuard, SuperAdminGuard, FeatureGuard } from '../guards';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import { LoadingScreen } from '../components/loading';
import PracticeDetails from '../pages/dashboard/practices/PracticeDetails';
import { CONFIGS } from '../pages/dashboard/_actions/configs';
import PracticeTreatmentList from '../pages/dashboard/practices/PracticeTreatment';
import PracticeUserList from '../pages/dashboard/practices/PracticeUser';
import { ConfirmEmail } from '../pages/auth';
import { lazyWithRetries } from './lazyRetry';

// Regression guard: the returned function MUST be a named function (not an anonymous
// arrow) so React's hook validator recognizes it as a valid component. The old anonymous
// arrow caused "Invalid hook call" (REACT-75, 17 users) because useLocation() was called
// in a function React didn't treat as a component. The ESLint disable is no longer needed.
const Loadable = (Component) => function LoadableComponent(props) {
    const { pathname } = useLocation();

    return (
        <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
            <Component {...props} />
        </Suspense>
    );
};

// Backward-compat: bookmarks at /dashboard/user/<anything> redirect to
// the equivalent /dashboard/practice/<anything> URL. The `/user/practice`
// → `/practice/details` exception is handled by a dedicated route above
// `<UserPathRedirect />` so this wildcard never sees it.
function UserPathRedirect() {
    const { pathname, search, hash } = useLocation();
    const newPath = pathname.replace(/^\/dashboard\/user\//, '/dashboard/practice/');
    return <Navigate to={`${newPath}${search}${hash}`} replace />;
}

// Backward-compat for `/dashboard/user/practice/:id` — preserves the :id
// segment when redirecting to `/dashboard/practice/details/:id`.
function UserPracticeIdRedirect() {
    const { id } = useParams();
    const { search, hash } = useLocation();
    return <Navigate to={`/dashboard/practice/details/${id}${search}${hash}`} replace />;
}

// AUTHENTICATION
const Login = Loadable(lazyWithRetries(() => import('../pages/auth/Login')));
const Register = Loadable(lazyWithRetries(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazyWithRetries(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazyWithRetries(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazyWithRetries(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazyWithRetries(() => import('../pages/dashboard/GeneralApp')));
const LeadDetailView = Loadable(lazyWithRetries(() => import('../pages/dashboard/LeadDetailView')));
const ArchiveDetailView = Loadable(lazyWithRetries(() => import('../pages/dashboard/ArchiveDetailView')));
const StripeAuthorized = Loadable(lazyWithRetries(() => import('../components/StripeAuthorized')));
const FacebookAuthorized = Loadable(lazyWithRetries(() => import('../components/FacebookAuthorized')));
const GoogleAuthorized = Loadable(lazyWithRetries(() => import('../components/GoogleAuthorized')));
const GoogleAdsAuthorized = Loadable(lazyWithRetries(() => import('../components/GoogleAdsAuthorized')));
const GoogleAnalyticsAuthorized = Loadable(lazyWithRetries(() => import('../components/GoogleAnalyticsAuthorized')));
const TikTokAuthorized = Loadable(lazyWithRetries(() => import('../components/TikTokAuthorized')));

// Tasks
const Tasks = Loadable(lazyWithRetries(() => import('../pages/dashboard/TasksPage')));
const TasksV2 = Loadable(lazyWithRetries(() => import('../pages/dashboard/TasksV2Page')));
const TasksV2DemoPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/TasksV2DemoPage')));
const TasksV2ActionsPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/TasksV2ActionsPage')));

// Forms
const FormsPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/FormsPage')));

// Storage
const StoragePage = Loadable(lazyWithRetries(() => import('../pages/dashboard/StoragePage')));

// Campaigns
const Campaigns = Loadable(lazyWithRetries(() => import('../pages/dashboard/CampaignsPage')));
const TemplateManager = Loadable(lazyWithRetries(() => import('../pages/dashboard/TemplateManagerPage')));
const CampaignBuilder = Loadable(lazyWithRetries(() => import('../pages/dashboard/CampaignBuilderPage')));
const ConsentBroadcastBuilder = Loadable(lazyWithRetries(() => import('../pages/dashboard/ConsentBroadcastBuilderPage')));
const CampaignReportPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/CampaignReportPage')));
const CampaignReportsListPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/CampaignReportsListPage')));
const CampaignAnalyticsDashboard = Loadable(lazyWithRetries(() => import('../pages/dashboard/CampaignAnalyticsDashboard')));
const CampaignTriggersPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/CampaignTriggersPage')));
const SmartSegmentsPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/SmartSegmentsPage')));

// FacebookComment
const FacebookComment = Loadable(lazyWithRetries(() => import('../pages/dashboard/ChatPage')));

// Whatsapp
const Whatsapp = Loadable(lazyWithRetries(() => import('../pages/whatsapp/Whatsapp')));

// Sms
const Sms = Loadable(lazyWithRetries(() => import('../pages/dashboard/Sms')));

// EMAIL
const Emails = Loadable(lazyWithRetries(() => import('../pages/dashboard/mail/mail')));

// CALL LOGS
const CallLogsDashboard = Loadable(lazyWithRetries(() => import('../pages/dashboard/CallLogsDashboard')));

// INSTAGRAM
const InstagramPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/InstagramPage')));

// Create Stripe Payment
const CreateStripePayment = Loadable(lazyWithRetries(() => import('../pages/dashboard/CreateStripePaymentPage')));

// ANALYTICS
const GeneralAnalytics = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/GeneralAnalytics')));
const TreatmentSummary = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/TreatmentSummary')));
const CategorySummary = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/CategorySummary')));
const SourceSummary = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/SourceSummary')));
const PractitionerSummary = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/PractitionerSummary')));
const ReportsSetting = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/ReportsSetting')));
const ArchiveSummary = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/ArchiveSummary')));
const AllPracticeReports = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/AllPracticeReports')));
const ContactLogs = Loadable(lazyWithRetries(() => import('../pages/dashboard/analytics/ContactLogs')));

// ANALYTICS REPORTS (New Section)
const AnalyticsReportsOverview = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsOverview')));
const AnalyticsReportsAllPractices = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsAllPractices')));
const AnalyticsReportsByTreatment = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsByTreatment')));
const AnalyticsReportsByPractitioner = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsByPractitioner')));
const AnalyticsReportsBySource = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsBySource')));
const AnalyticsReportsTrends = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsTrends')));
const AnalyticsReportsWhatsApp = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsWhatsApp')));
const AnalyticsReportsSettings = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsSettings')));
const AnalyticsReportsLostLeads = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsLostLeads')));
const AnalyticsReportsByTeam = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsByTeam')));
const AnalyticsReportsMetaAds = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsMetaAds')));
const AnalyticsReportsMarketing = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsMarketing')));
const AnalyticsReportsCampaignConfig = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/AnalyticsReportsCampaignConfig')));
const DentallyInsights = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/DentallyInsights')));
const ClickTracking = Loadable(lazyWithRetries(() => import('../pages/dashboard/analyticsReports/ClickTracking')));

// GROUP DASHBOARD
const GroupDashboard = Loadable(lazyWithRetries(() => import('../pages/dashboard/groupDashboard/GroupDashboard')));
const GroupManagePage = Loadable(lazyWithRetries(() => import('../pages/dashboard/groupDashboard/GroupManagePage')));

// GOOGLE ADS
const GoogleAds = Loadable(lazyWithRetries(() => import('../pages/dashboard/googleAds/GoogleAds')));

// GOOGLE ANALYTICS
const GoogleAnalytics = Loadable(lazyWithRetries(() => import('../pages/dashboard/googleAnalytics/GoogleAnalytics')));

// TABLE
const AllLeadTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/AllLeadTable/AllLeadsTable')));
const InterPractice = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/AllLeadTable/InterPractice')));
const AllPatients = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/AllPatients')));
const DentallyPatients = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/DentallyPatients')));
const NoTaskTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/AllLeadTable/NoTaskTable')));
const NewLeadTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/NewLeadTable')));
const NHSLeadTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/NHSLeadTable')));
const ArchiveTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/ArchiveLeadTable')));
const ConsultantAttendTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/ConsultantAttend')));
const ConsultantAttendV2Table = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/ConsultantAttendV2')));
const ConsultantBookedTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/ConsultantBooked')));
const TreatmentStartedTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/TreatmentStarted')));
const FailedToAttendTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/FailedToAttendTable')));
const ClosedTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/ClosedTable')));
const AlignerApproveTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/AlignerApprove')));
const AttendedChecklistsTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/AttendedChecklists')));

// USER
const Message = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/MessageDetails')));

// USER
const UserProfile = Loadable(lazyWithRetries(() => import('../pages/dashboard/UserProfile')));
const UserCreate = Loadable(lazyWithRetries(() => import('../pages/dashboard/UserCreate')));
const InviteUser = Loadable(lazyWithRetries(() => import('../pages/dashboard/InviteUserCreate')));

// APP
const Calendar = Loadable(lazyWithRetries(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazyWithRetries(() => import('../pages/dashboard/Kanban')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazyWithRetries(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const Maintenance = Loadable(lazyWithRetries(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazyWithRetries(() => import('../pages/Page500')));
const Page403 = Loadable(lazyWithRetries(() => import('../pages/Page403')));
const Page404 = Loadable(lazyWithRetries(() => import('../pages/Page404')));
const ComingSoonPage = Loadable(lazyWithRetries(() => import('../pages/ComingSoon')));
const PrivacyPolicy = Loadable(lazyWithRetries(() => import('../pages/PrivacyPolicy')));
const AppointmentRating = Loadable(lazyWithRetries(() => import('../pages/AppointmentRating')));
const ConsentFormPublic = Loadable(lazyWithRetries(() => import('../pages/public/ConsentFormPublic')));
const ConsentBundlePage = Loadable(lazyWithRetries(() => import('../pages/public/ConsentBundlePage')));
const PatientHubNotFound = Loadable(lazyWithRetries(() => import('../pages/public/PatientHubNotFound')));
const AlignerApproveStatusMail = Loadable(lazyWithRetries(() => import('../pages/AlignerApproveStatusMail')));
const OnlineBookingPage = Loadable(lazyWithRetries(() => import('../pages/public/booking/OnlineBookingPage')));

// PAYMENT
const Integration = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/practiceIntegration')));

// CONSENT
const Consent = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/Consent')));

// LOAN
const Loan = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/Loan')));

// NOTIFICATION
const Notification = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/NotificationSetting')));
const NotificationPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/NotificationTable')));

// EXPORT TO CSV
const ExportCsv = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/ExportCsv')));

// PRACTICE CHATBOT
const PracticeChatbot = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/PracticeChatbot')));

// PRACTICE PREFERENCE
const PracticePreferences = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/PracticePreferences')));
const AutoRemindersTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/AutoRemindersTable')));
const CreateAutoReminder = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/CreateAutoReminder')));
const EditAutoReminder = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/EditAutoReminder')));
const CallScripts = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/CallScripts')));
const CallScriptsV2 = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/callScriptV2/CallScriptV2')));
const CreateScript = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/CreateScript')));
const ChatBotForm = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/ChatBot/ChatBotForm')));
const EditCallScript = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/EditCallScript')));
const ConsentBuilder = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/ConsentBuilder')));
const CreateConsent = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/CreateConsent')));
const EditConsent = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/EditConsent')));
const PractitionerSchedule = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/PractitionerSchedule')));
const PracticeFormSettings = Loadable(lazyWithRetries(() => import('../pages/dashboard/practices/PracticeFormSettings')));
// Track E (template convergence — 2026-04-26): the standalone Form Delivery
// Templates settings page was retired; templates are now managed via
// /dashboard/campaigns-manager/templates with the Form Delivery purpose chip.

// SUPER ADMIN
const EngagementTemplates = Loadable(lazyWithRetries(() => import('../pages/superAdmin/Engagement/ListTemplate')));
const EmailEngagementTemplates = Loadable(lazyWithRetries(() => import('../pages/superAdmin/Engagement/EmailTemplate')));
const SmsEngagementTemplates = Loadable(lazyWithRetries(() => import('../pages/superAdmin/Engagement/SmsTemplate')));
const BulkAutoReminders = Loadable(lazyWithRetries(() => import('../pages/superAdmin/Engagement/AutoReminderTemplate')));
const CreateBulkAutoReminder = Loadable(lazyWithRetries(() => import('../pages/superAdmin/Engagement/CreateAutoReminderTemplate')));
const EditBulkAutoReminder = Loadable(lazyWithRetries(() => import('../pages/superAdmin/Engagement/EditAutoReminderTemplate')));
const PracticeChoices = Loadable(lazyWithRetries(() => import('../pages/superAdmin/PracticeChoices')));
const UserManagement = Loadable(lazyWithRetries(() => import('../pages/superAdmin/UserManagement')));
const DomainManagement = Loadable(lazyWithRetries(() => import('../pages/superAdmin/DomainManagement')));
const FeatureFlags = Loadable(lazyWithRetries(() => import('../pages/superAdmin/FeatureFlags')));
const TasksV2MigrationList = Loadable(lazyWithRetries(() => import('../pages/superAdmin/TasksV2Migration')));
const TasksV2MigrationCockpit = Loadable(lazyWithRetries(() => import('../pages/superAdmin/TasksV2Migration/PracticeCockpit')));
const TableDefaults = Loadable(lazyWithRetries(() => import('../pages/superAdmin/TableDefaults')));
const TableDefaultsRedesignPreview = Loadable(lazyWithRetries(() => import('../pages/superAdmin/TableDefaultsRedesignPreview')));
const SidebarNavDefaults = Loadable(lazyWithRetries(() => import('../pages/superAdmin/SidebarNavDefaults')));
const SystemTaskTemplates = Loadable(lazyWithRetries(() => import('../pages/superAdmin/SystemTaskTemplates')));
const NotificationManagement = Loadable(lazyWithRetries(() => import('../pages/superAdmin/NotificationManagement')));
const NotificationManagementHelp = Loadable(lazyWithRetries(() => import('../pages/superAdmin/NotificationManagementHelp')));
const CreatePractice = Loadable(lazyWithRetries(() => import('../pages/superAdmin/CreatePractice')));
const ConsentV2List = Loadable(lazyWithRetries(() => import('../pages/superAdmin/ConsentV2/ConsentV2List')));
const ConsentV2Builder = Loadable(lazyWithRetries(() => import('../pages/superAdmin/ConsentV2/ConsentV2Builder')));
const DuplicateLeadTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/DuplicateLeads')));
const SpamLeadTable = Loadable(lazyWithRetries(() => import('../pages/dashboard/tables/SpamLeads')));
const AudienceManager = Loadable(lazyWithRetries(() => import('../pages/dashboard/AudienceManager')));
const CreateAudience = Loadable(lazyWithRetries(() => import('../pages/dashboard/CreateAudience')));
const EditAudience = Loadable(lazyWithRetries(() => import('../pages/dashboard/EditAudience')));
const ImportLeads = Loadable(lazyWithRetries(() => import('../pages/dashboard/ImportLeads')));
const ImportLeadsCreate = Loadable(lazyWithRetries(() => import('../pages/dashboard/ImportLeadsCreate')));
const ReviewPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/ReviewPage')));

// Financial
const Financial = Loadable(lazyWithRetries(() => import('../pages/dashboard/FinancialTracker')));
const QrCode = Loadable(lazyWithRetries(() => import('../pages/dashboard/QRCodeWrapper')));
const SurveyList = Loadable(lazyWithRetries(() => import('../pages/dashboard/Survey/SurveyList')));
const SurveyFormPublic = Loadable(lazyWithRetries(() => import('../pages/public/SurveyFormPublic')));
const BlankRedirect = Loadable(lazyWithRetries(() => import('../pages/BlankRedirect')));


const GeoLocator = Loadable(lazyWithRetries(() => import('../pages/dashboard/GeoLocator')));
const Planner = Loadable(lazyWithRetries(() => import('../pages/dashboard/Planner')));
const SubmitMarketingTask = Loadable(lazyWithRetries(() => import('../pages/dashboard/SubmitMarketingTask')));
const WebsiteSupportWrapper = Loadable(lazyWithRetries(() => import('../pages/dashboard/WebsiteSupportWrapper')));
const AnnotationSessionPicker = Loadable(lazyWithRetries(() => import('../pages/dashboard/AnnotationSessionPicker')));
const FormSubmissionsPage = Loadable(lazyWithRetries(() => import('../pages/dashboard/FormSubmissions/FormSubmissionsPage')));
const ManageBusiness = Loadable(lazyWithRetries(() => import('../pages/dashboard/ManageBusiness')));
const GooglePosts = Loadable(lazyWithRetries(() => import('../pages/dashboard/GooglePosts')));
// Helper: check if we're on patient-hub.co.uk or any of its subdomains
const isPatientHubDomain = () => {
    const { hostname } = window.location;
    return hostname === 'patient-hub.co.uk' || hostname.endsWith('.patient-hub.co.uk');
};

// Public-only routes for patient-hub domain (no CRM access, no CRM branding)
const patientHubRoutes = [
    { path: 'privacy-policy', element: <PrivacyPolicy /> },
    { path: 'appointments/feedback', element: <AppointmentRating /> },
    { path: 'consent/:accessToken', element: <ConsentFormPublic /> },
    { path: 'consent/:encodedParams', element: <ConsentFormPublic /> },
    { path: 'consent/form/:formUuid', element: <ConsentFormPublic /> },
    { path: 'consent/bundle', element: <ConsentBundlePage /> },
    { path: 'forms/:accessToken', element: <ConsentFormPublic /> },
    { path: 'forms/bundle', element: <ConsentBundlePage /> },
    { path: 'survey/:slug', element: <SurveyFormPublic /> },
    { path: 'lead-treatments/aligner-approve/change-status', element: <AlignerApproveStatusMail /> },
    { path: '/', element: <PatientHubNotFound /> },
    { path: '*', element: <PatientHubNotFound /> },
];

export default function Router() {
    // On patient-hub.co.uk (and subdomains), only serve public routes — no CRM access
    const onPatientHub = isPatientHubDomain();

    return useSentryRoutes(onPatientHub ? patientHubRoutes : [
        {
            path: 'auth',
            children: [
                {
                    path: 'login',
                    element: (
                        <GuestGuard>
                            <Login />
                        </GuestGuard>
                    ),
                },
                {
                    path: 'register',
                    children: [
                        { element: <GuestGuard><Register /></GuestGuard>, index: true },
                        { path: 'account-confirm-email/:keyId', element: <ConfirmEmail /> }
                    ]
                },
                {
                    path: 'login-unprotected',
                    element: (
                        <GuestGuard>
                            <Login />
                        </GuestGuard>
                    )
                },
                {
                    path: 'register-unprotected',
                    element: (
                        <GuestGuard>
                            <Register />
                        </GuestGuard>
                    )
                },
                {
                    element: <CompactLayout />,
                    children: [
                        { path: 'reset-password', element: <ResetPassword /> },
                        { path: 'verify', element: <VerifyCode /> },
                    ]
                },
                // OAuth callback path used by prod: /auth/google/callback
                { path: 'google/callback', element: <GoogleAuthorized /> },
                { path: 'google/callback/', element: <GoogleAuthorized /> },
                { path: 'google/ads/callback', element: <GoogleAdsAuthorized /> },
                { path: 'google/ads/callback/', element: <GoogleAdsAuthorized /> },
                { path: 'google-analytics/callback', element: <GoogleAnalyticsAuthorized /> },
                { path: 'google-analytics/callback/', element: <GoogleAnalyticsAuthorized /> },
                { path: 'google/analytics/authorize/callback', element: <GoogleAnalyticsAuthorized /> },
                { path: 'google/analytics/authorize/callback/', element: <GoogleAnalyticsAuthorized /> },
            ],
        },
        {
            path: 'qr/:shortURI',
            element: (
                <BlankRedirect />
            ),
        },
        // Google Ads OAuth callback (backend redirects to /google-ads/callback?code=...)
        {
            path: 'google-ads',
            children: [
                { path: 'callback', element: <GoogleAdsAuthorized /> },
                { path: 'callback/', element: <GoogleAdsAuthorized /> },
            ],
        },
        // Google Analytics OAuth callback (backend redirects to /google-analytics/callback?code=...)
        {
            path: 'google-analytics',
            children: [
                { path: 'callback', element: <GoogleAnalyticsAuthorized /> },
                { path: 'callback/', element: <GoogleAnalyticsAuthorized /> },
            ],
        },
        // Dashboard Routes
        {
            path: 'stripe',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                    <IntervalComponentV3 />
                </AuthGuard>
            ),
            children: [
                { path: 'authorize/callback/', element: <StripeAuthorized /> },
            ]
        },
        {
            path: 'facebook',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                    <IntervalComponentV3 />
                </AuthGuard>
            ),
            children: [
                { path: 'authorize/callback/', element: <FacebookAuthorized /> },
            ]
        },
        {
            path: 'google',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                    <IntervalComponentV3 />
                </AuthGuard>
            ),
            children: [
                { path: 'authorize/callback/', element: <GoogleAuthorized /> },
                { path: 'ads/authorize/callback/', element: <GoogleAdsAuthorized /> },
                { path: 'analytics/authorize/callback/', element: <GoogleAnalyticsAuthorized /> },
            ]
        },
        {
            path: 'tiktok',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                    <IntervalComponentV3 />
                </AuthGuard>
            ),
            children: [
                { path: 'authorize/callback/', element: <TikTokAuthorized /> },
            ]
        },
        {
            path: 'dashboard',
            element: (
                <AuthGuard>
                    <DashboardLayout />
                    <IntervalComponentV3 />
                </AuthGuard>
            ),
            children: [
                { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
                { path: 'app', element: <GeneralApp /> },
                { path: 'practice/:practiceId/lead-detail/:id/:tab?', element: <LeadDetailView /> },
                { path: 'practice/:practiceId/archived/:id/', element: <ArchiveDetailView /> },
                { path: 'stripe/authorize/callback/', element: <StripeAuthorized /> },
                { path: 'facebook/authorize/callback/', element: <FacebookAuthorized /> },
                { path: 'google/authorize/callback/', element: <GoogleAuthorized /> },
                { path: 'google/ads/authorize/callback/', element: <GoogleAdsAuthorized /> },
                { path: 'google/analytics/authorize/callback/', element: <GoogleAnalyticsAuthorized /> },
                { path: 'tiktok/authorize/callback/', element: <TikTokAuthorized /> },
                { path: 'tasks', element: <Tasks /> },
                { path: 'tasks-v2', element: <TasksV2 /> },
                { path: 'practice/:practiceId/tasks-v2', element: <TasksV2 /> },
                { path: 'practice/:practiceId/tasks-v2/templates', element: <SuperAdminGuard><TasksV2 /></SuperAdminGuard> },
                { path: 'practice/:practiceId/tasks-v2/templates/new', element: <SuperAdminGuard><TasksV2 /></SuperAdminGuard> },
                { path: 'practice/:practiceId/tasks-v2/templates/:templateId/edit', element: <SuperAdminGuard><TasksV2 /></SuperAdminGuard> },
                { path: 'practice/:practiceId/tasks-v2/_demo', element: <TasksV2DemoPage /> },
                { path: 'practice/:practiceId/tasks-v2/_actions', element: <SuperAdminGuard><TasksV2ActionsPage actionConfigs={CONFIGS} /></SuperAdminGuard> },
                { path: 'practice/:practiceId/tasks-v2/:taskId', element: <TasksV2 /> },
                { path: 'forms', element: <FormsPage /> },
                { path: 'storage', element: <StoragePage /> },
                {
                    path: 'practice/:practiceId/whatsapp',
                    element: <Whatsapp />,
                    children: [
                        { index: true, element: null },
                        { path: 'thread/:threadId', element: null }
                    ]
                },
                { path: 'sms', element: <Sms /> },
                { path: 'emails', element: <Emails /> },
                { path: 'call-logs', element: <CallLogsDashboard /> },
                { path: 'practice/:practiceId/instagram', element: <InstagramPage /> },
                { path: 'payment-links/create', element: <CreateStripePayment /> },
                { path: 'manage-business', element: <ManageBusiness /> },
                { path: 'google-posts', element: <GooglePosts /> },
                {
                    path: 'analytics',
                    children: [
                        { element: <Navigate to="/dashboard/analytics/reports" replace />, index: true },
                        { path: 'reports', element: <GeneralAnalytics /> },
                        { path: 'treatment-summary', element: <TreatmentSummary /> },
                        { path: 'category-summary', element: <CategorySummary /> },
                        { path: 'contact-summary', element: <Navigate to="/dashboard/analytics-reports/click-tracking" replace /> },
                        { path: 'contact-logs', element: <ContactLogs /> },
                        { path: 'source-summary', element: <SourceSummary /> },
                        { path: 'practitioner-summary', element: <PractitionerSummary /> },
                        { path: 'reports-setting', element: <ReportsSetting /> },
                        { path: 'archived-summary', element: <ArchiveSummary /> },
                        { path: 'allpractice-reports', element: <AllPracticeReports /> },
                    ]
                },
                {
                    path: 'analytics-reports',
                    children: [
                        { element: <Navigate to="/dashboard/analytics-reports/overview" replace />, index: true },
                        { path: 'overview', element: <AnalyticsReportsOverview /> },
                        { path: 'all-practices', element: <AnalyticsReportsAllPractices /> },
                        { path: 'by-treatment', element: <AnalyticsReportsByTreatment /> },
                        { path: 'by-practitioner', element: <AnalyticsReportsByPractitioner /> },
                        { path: 'by-source', element: <AnalyticsReportsBySource /> },
                        { path: 'trends', element: <AnalyticsReportsTrends /> },
                        { path: 'whatsapp', element: <AnalyticsReportsWhatsApp /> },
                        { path: 'google-ads', element: <GoogleAds /> },
                        { path: 'google-analytics', element: <GoogleAnalytics /> },
                        { path: 'meta-ads', element: <AnalyticsReportsMetaAds /> },
                        // Super-admin gated at the route level — not just
                        // the API + nav-link level — so a typed URL also
                        // redirects to /permission-denied. Phase 0 only;
                        // promote to ADMIN/SUPPORT in Phase 1 by swapping
                        // SuperAdminGuard for an appropriate role guard.
                        { path: 'marketing', element: <SuperAdminGuard><AnalyticsReportsMarketing /></SuperAdminGuard> },
                        { path: 'campaigns', element: <SuperAdminGuard><AnalyticsReportsCampaignConfig /></SuperAdminGuard> },
                        { path: 'facebook-ads', element: <Navigate to="/dashboard/analytics-reports/meta-ads" replace /> },
                        { path: 'lost-leads', element: <AnalyticsReportsLostLeads /> },
                        { path: 'by-team', element: <AnalyticsReportsByTeam /> },
                        { path: 'geo-locator', element: <FeatureGuard feature="geo-locator" level="view" fallback="redirect"><GeoLocator /></FeatureGuard> },
                        { path: 'dentally-insights', element: <DentallyInsights /> },
                        { path: 'click-tracking', element: <SuperAdminGuard><ClickTracking /></SuperAdminGuard> },
                        { path: 'settings', element: <AnalyticsReportsSettings /> },
                    ]
                },
                { path: 'group-dashboard', element: <GroupDashboard /> },
                {
                    path: 'tables',
                    children: [
                        { element: <Navigate to="/dashboard/tables/all-lead-table" replace />, index: true },
                        { path: 'all-lead-table', element: <AllLeadTable /> },
                        { path: 'inter-practice-table', element: <InterPractice /> },
                        { path: 'all-patients', element: <AllPatients /> },
                        { path: 'dentally-patients', element: <FeatureGuard feature="dentally-integration" level="view" fallback="redirect"><DentallyPatients /></FeatureGuard> },
                        { path: 'no-task-assigned-table', element: <NoTaskTable /> },
                        { path: 'new-lead-table', element: <NewLeadTable /> },
                        { path: 'nhs-lead-table', element: <NHSLeadTable /> },
                        { path: 'archive-table', element: <ArchiveTable /> },
                        { path: 'consultant-attended-table', element: <ConsultantAttendTable /> },
                        { path: 'consultation-attended-v2', element: <FeatureGuard feature="ca-pipeline-v2" level="view" fallback="redirect"><ConsultantAttendV2Table /></FeatureGuard> },
                        { path: 'consultant-booked-table', element: <ConsultantBookedTable /> },
                        { path: 'treatment-started-table', element: <TreatmentStartedTable /> },
                        { path: 'aligner-approve-table', element: <FeatureGuard feature="aligner-approve" level="view" fallback="redirect"><AlignerApproveTable /></FeatureGuard> },
                        { path: 'failed-to-attend-table', element: <FailedToAttendTable /> },
                        { path: 'completed-table', element: <ClosedTable /> },
                        { path: 'duplicate-lead-table', element: <DuplicateLeadTable /> },
                        { path: 'spam-lead-table', element: <SpamLeadTable /> },
                        { path: 'attended-checklists-table', element: <FeatureGuard feature="tasks-v2" level="view" fallback="redirect"><AttendedChecklistsTable /></FeatureGuard> },
                    ],
                },
                {
                    // Practice-scoped admin pages. Renamed from /user/ to
                    // /practice/ so the URL prefix reflects what these pages
                    // actually configure. `/user/practice` (practice details)
                    // becomes `/practice/details` to avoid `/practice/practice`.
                    path: 'practice',
                    children: [
                        { element: <Navigate to="/dashboard/practice/profile" replace />, index: true },
                        { path: 'profile', element: <UserProfile /> },
                        { path: 'details', element: <PracticeDetails /> },
                        { path: 'details/:id', element: <PracticeDetails /> },
                        { path: 'practice-treatment', element: <PracticeTreatmentList /> },
                        { path: 'practice-user', element: <PracticeUserList /> },
                        { path: 'new', element: <InviteUser /> },
                        { path: ':id/edit', element: <UserCreate /> },
                        { path: 'message', element: <Message /> },
                        { path: 'integration', element: <Integration /> },
                        { path: 'practice-consent', element: <Consent /> },
                        { path: 'form-settings', element: <PracticeFormSettings /> },
                        { path: 'practice-loan', element: <Loan /> },
                        { path: 'notification-settings', element: <Notification /> },
                        { path: 'notification-table', element: <NotificationPage /> },
                        { path: 'practice-preferences', element: <PracticePreferences /> },
                        { path: 'export-csv', element: <ExportCsv /> },
                        { path: 'practice-chatbot', element: <FeatureGuard feature="chatbot"><PracticeChatbot /></FeatureGuard> },
                        { path: 'auto-reminders', element: <AutoRemindersTable /> },
                        { path: 'create-reminder', element: <CreateAutoReminder /> },
                        { path: ':id/edit-reminder', element: <EditAutoReminder /> },
                        { path: 'facebook', element: <FacebookComment /> },
                        { path: 'call-scripts', element: <CallScripts /> },
                        { path: 'call-scripts-v2', element: <CallScriptsV2 /> },
                        { path: 'create-script', element: <CreateScript /> },
                        { path: 'create-chatbot', element: <ChatBotForm title='Create chatbot' /> },
                        { path: ':id/edit-script', element: <EditCallScript /> },
                        { path: 'edit-chatbot/:id', element: <ChatBotForm title='Edit chatbot' /> },
                        { path: 'consent', element: <ConsentBuilder /> },
                        { path: 'create-consent', element: <CreateConsent /> },
                        { path: ':id/edit-consent', element: <EditConsent /> },
                        { path: 'practitioner-schedule/:practitionerId', element: <PractitionerSchedule /> },
                    ],
                },
                // Backward-compat: any bookmarked /dashboard/user/<anything>
                // URL redirects to the equivalent /dashboard/practice/ path.
                // Special-case /user/practice → /practice/details since
                // the JS-side path changed shape there.
                { path: 'user', element: <Navigate to="/dashboard/practice/profile" replace /> },
                { path: 'user/practice', element: <Navigate to="/dashboard/practice/details" replace /> },
                { path: 'user/practice/:id', element: <UserPracticeIdRedirect /> },
                { path: 'user/*', element: <UserPathRedirect /> },
                { path: 'calendar', element: <Calendar /> },
                { path: 'kanban', element: <Kanban /> },
                { path: 'permission-denied', element: <PermissionDenied /> },
                // Track E: ``/dashboard/settings/form-delivery-templates``
                // route was removed on 2026-04-26. The Send Forms wizard's
                // "Edit templates" link now opens
                // ``/dashboard/campaigns-manager/templates?purpose=FORM_DELIVERY``.
                {
                    path: 'super-admin',
                    element: <SuperAdminGuard><Outlet /></SuperAdminGuard>,
                    children: [
                        { element: <Navigate to="/dashboard/super-admin/engagement_templates" replace />, index: true },
                        { path: 'engagement_templates', element: <EngagementTemplates /> },
                        { path: 'email_template/:templateId?', element: <EmailEngagementTemplates /> },
                        { path: 'sms_template/:templateId?', element: <SmsEngagementTemplates /> },
                        { path: 'auto_reminder', element: <BulkAutoReminders /> },
                        { path: 'create_reminder', element: <CreateBulkAutoReminder /> },
                        { path: ':id/edit_reminder', element: <EditBulkAutoReminder /> },
                        { path: 'similar-duplicate-lead-table', element: <DuplicateLeadTable /> },
                        { path: 'import', element: <ImportLeads /> },
                        { path: 'create_import_lead', element: <ImportLeadsCreate /> },
                        { path: 'practice-choices', element: <PracticeChoices /> },
                        { path: 'user-management', element: <UserManagement /> },
                        { path: 'domain-management', element: <DomainManagement /> },
                        { path: 'feature-flags', element: <FeatureFlags /> },
                        { path: 'tasks-v2-migration', element: <TasksV2MigrationList /> },
                        { path: 'tasks-v2-migration/:practiceId', element: <TasksV2MigrationCockpit /> },
                        { path: 'table-defaults', element: <TableDefaults /> },
                        { path: 'table-defaults-preview', element: <TableDefaultsRedesignPreview /> },
                        { path: 'sidebar-navigation', element: <SidebarNavDefaults /> },
                        { path: 'group-dashboard', element: <GroupManagePage /> },
                        { path: 'create-practice', element: <CreatePractice /> },
                        { path: 'consent', element: <ConsentBuilder /> },
                        { path: 'form-builder', element: <ConsentV2List /> },
                        { path: 'form-builder/create', element: <ConsentV2Builder /> },
                        { path: 'form-builder/:id/edit', element: <ConsentV2Builder /> },
                        { path: 'system-task-templates', element: <SystemTaskTemplates /> },
                        { path: 'notification-management', element: <NotificationManagement /> },
                        { path: 'notification-management/help', element: <NotificationManagementHelp /> },
                    ]
                },
                { path: 'patient-review', element: <ReviewPage /> },
                { path: 'financial-tracker', element: <Financial /> },
                { path: 'qr-code', element: <QrCode /> },
                { path: 'survey', element: <SurveyList /> },
                {
                    path: 'campaigns-manager',
                    children: [
                        { element: <Navigate to="/dashboard/campaigns-manager/campaigns" replace />, index: true },
                        { path: 'campaigns', element: <Campaigns /> },
                        { path: 'audience_manager', element: <AudienceManager /> },
                        { path: 'create_audience', element: <CreateAudience /> },
                        { path: ':id/edit_audience', element: <EditAudience /> },
                        { path: 'templates', element: <TemplateManager /> },
                        { path: 'builder', element: <CampaignBuilder /> },
                        { path: 'builder/:campaignId', element: <CampaignBuilder /> },
                        { path: 'campaigns/:campaignId/report', element: <CampaignReportPage /> },
                        { path: 'reports', element: <CampaignAnalyticsDashboard /> },
                        { path: 'reports-list', element: <CampaignReportsListPage /> },
                        { path: 'triggers', element: <CampaignTriggersPage /> },
                        { path: 'segments', element: <SmartSegmentsPage /> },
                        { path: 'consent-broadcast', element: <ConsentBroadcastBuilder /> },
                    ],
                },

                // Geo Locator moved under /analytics-reports/geo-locator as a Reporting tab.
                { path: 'planner', element: <Planner /> },
                { path: 'marketing-request', element: <SubmitMarketingTask /> },
                { path: 'website-annotation', element: <WebsiteSupportWrapper /> },
                { path: 'website-annotation/:sessionId', element: <WebsiteSupportWrapper /> },
                { path: 'website-annotation/:sessionId/edit', element: <WebsiteSupportWrapper /> },
                { path: 'annotation-sessions', element: <AnnotationSessionPicker /> },
                { path: 'form-submissions', element: <FormSubmissionsPage /> }
            ],
        },

        // Main Routes
        {
            path: '*',
            element: <CompactLayout />,
            children: [
                { path: 'maintenance', element: <Maintenance /> },
                { path: 'reset/:uid/:token', element: <NewPassword /> },
                { path: '500', element: <Page500 /> },
                { path: '404', element: <Page404 /> },
                { path: '403', element: <Page403 /> },
                { path: 'coming-soon', element: <ComingSoonPage /> },
                { path: '*', element: <Navigate to="/404" replace /> },
            ],
        },
        {
            path: '/',
            element: <MainLayout />,
            children: [
                { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
            ],
        },
        { path: '*', element: <Navigate to="/404" replace /> },
        { path: 'privacy-policy', element: <PrivacyPolicy /> },
        { path: 'appointments/feedback', element: <AppointmentRating /> },
        { path: 'consent/:accessToken', element: <ConsentFormPublic /> },
        { path: 'consent/:encodedParams', element: <ConsentFormPublic /> },
        { path: 'lead-treatments/aligner-approve/change-status', element: <AlignerApproveStatusMail /> },
        { path: 'consent/form/:formUuid', element: <ConsentFormPublic /> },
        { path: 'consent/bundle', element: <ConsentBundlePage /> },
        { path: 'forms/:accessToken', element: <ConsentFormPublic /> },
        { path: 'forms/bundle', element: <ConsentBundlePage /> },
        { path: 'survey/:slug', element: <SurveyFormPublic /> },
        { path: 'book/:practiceSlug', element: <OnlineBookingPage /> },
    ]);
}
